import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, CircleNotch, Package } from "@phosphor-icons/react";
import { api, money } from "../lib/api";
import { useCart } from "../context/CartContext";

const MAX_ATTEMPTS = 6;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed | expired
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    let timer;

    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        setOrder(res.data);
        if (res.data.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (res.data.status === "expired") {
          setState("expired");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setState("failed");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        setState("failed");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div className="max-w-2xl mx-auto px-5 py-28 text-center" data-testid="payment-result">
      {state === "checking" && (
        <>
          <CircleNotch size={56} className="mx-auto text-[#D97736] animate-spin" />
          <h1 className="font-display text-5xl mt-6">Verificando tu pago</h1>
          <p className="text-gray-400 mt-3">Un momento, estamos confirmando la transacción...</p>
        </>
      )}

      {state === "paid" && (
        <>
          <CheckCircle size={64} weight="fill" className="mx-auto text-[#25D366]" data-testid="payment-success-icon" />
          <h1 className="font-display text-6xl mt-6">¡Salud!</h1>
          <p className="text-gray-300 mt-3">Tu pago se realizó con éxito. Prepararemos tu pedido enseguida.</p>
          {order && (
            <div className="mt-8 bg-[#171717] border border-white/10 p-6 text-left">
              <div className="overline mb-4 flex items-center gap-2"><Package size={16} /> Detalle del pedido</div>
              {(order.items || []).map((it, i) => (
                <div key={it.beer_id || i} className="flex justify-between py-2 border-b border-white/5 text-sm">
                  <span>{it.quantity}x {it.name}</span>
                  <span>{money(it.price * it.quantity)}</span>
                </div>
              ))}
              <div className="flex justify-between items-end pt-4">
                <span className="overline">Total pagado</span>
                <span className="font-display text-3xl text-[#D97736]">{money(order.amount)}</span>
              </div>
            </div>
          )}
          <Link to="/catalogo" className="inline-block mt-8 bg-[#D97736] hover:bg-[#E88D50] text-black font-semibold uppercase tracking-wide px-8 py-4 transition-colors">
            Seguir comprando
          </Link>
        </>
      )}

      {(state === "failed" || state === "expired") && (
        <>
          <XCircle size={64} weight="fill" className="mx-auto text-red-500" />
          <h1 className="font-display text-5xl mt-6">{state === "expired" ? "Sesión expirada" : "No pudimos confirmar el pago"}</h1>
          <p className="text-gray-400 mt-3">Si el cargo se realizó, contáctanos. De lo contrario, intenta de nuevo.</p>
          <Link to="/carrito" className="inline-block mt-8 border border-white/25 hover:border-[#D97736] hover:text-[#D97736] uppercase tracking-wide px-8 py-4 transition-colors">
            Volver al carrito
          </Link>
        </>
      )}
    </div>
  );
}
